import { useCallback, useEffect, useRef, useState } from "react";

import dayjs from "dayjs";

import { fetchPlace } from "@/apis/place";
import { batchCreate, batchPreview } from "@/apis/reservation";
import Button from "@/components/Button";
import type { PlacesByFloor } from "@/dummy/places";
import BatchPreviewTable from "@/pages/Reservation/components/ReservationTab/BatchReservationTab/BatchPreviewTable";
import ConflictManageModal from "@/pages/Reservation/components/ReservationTab/BatchReservationTab/ConflictManageModal";
import {
  BUILDINGS,
  type FormState,
  TIME_OPTIONS,
  formatTime,
  initialForm,
  inputCls,
} from "@/pages/Reservation/components/ReservationTab/BatchReservationTab/constants";
import useLoginStore from "@/store/loginStore";
import useToastStore from "@/store/toastStore";
import type { BatchPreviewResult, RepeatType } from "@/types/batchReservation";
import { DAY_OF_WEEK_LABEL } from "@/types/batchReservation";

const BatchReservationTab = () => {
  const { tokenId } = useLoginStore();
  const showToast = useToastStore(state => state.showToast);
  const [form, setForm] = useState<FormState>(initialForm);
  const [placeList, setPlaceList] = useState<PlacesByFloor[]>([]);
  const [previewResults, setPreviewResults] = useState<BatchPreviewResult[]>(
    [],
  );
  const [includeDates, setIncludeDates] = useState<Set<string>>(new Set());
  const [isPreviewed, setIsPreviewed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [isConflictModalOpen, setIsConflictModalOpen] = useState(false);
  const autoPreviewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    loadPlaces(form.bldCd);
  }, [form.bldCd]);

  const loadPlaces = async (bldCd: string) => {
    const res = await fetchPlace(bldCd);
    setPlaceList(res ?? []);
    setForm(prev => ({ ...prev, plcCd: "" }));
    resetPreview();
  };

  const resetPreview = () => {
    setIsPreviewed(false);
    setPreviewResults([]);
  };

  const flatPlaces = placeList.flatMap(floor => floor.places);

  const handleChange = <K extends keyof FormState>(
    key: K,
    value: FormState[K],
  ) => {
    setForm(prev => ({ ...prev, [key]: value }));
    resetPreview();
  };

  const toggleRepeatDay = (day: string) => {
    setForm(prev => {
      const days = prev.repeatDays.includes(day)
        ? prev.repeatDays.filter(d => d !== day)
        : [...prev.repeatDays, day];

      return { ...prev, repeatDays: days };
    });
    resetPreview();
  };

  const buildRepeatDays = useCallback((): string => {
    if (form.repeatType === "daily") return "";
    if (form.repeatType === "weekly") return form.repeatDays.join(",");
    return form.monthlyDays;
  }, [form.repeatType, form.repeatDays, form.monthlyDays]);

  const isFormValid = useCallback((): boolean => {
    if (!form.plcCd) return false;
    if (!form.startDate || !form.endDate) return false;
    if (form.repeatType === "weekly" && form.repeatDays.length === 0)
      return false;
    if (form.repeatType === "monthly" && !form.monthlyDays.trim()) return false;
    if (form.startTime >= form.endTime) return false;
    return true;
  }, [form]);

  const requestPreview = useCallback(async (): Promise<
    BatchPreviewResult[] | null
  > => {
    if (!isFormValid()) return null;

    setPreviewError("");
    setIsLoading(true);
    try {
      const results = await batchPreview({
        cpsCd: "PTK",
        bldCd: form.bldCd,
        plcCd: form.plcCd,
        startDate: dayjs(form.startDate).format("YYYYMMDD"),
        endDate: dayjs(form.endDate).format("YYYYMMDD"),
        repeatType: form.repeatType,
        repeatDays: buildRepeatDays(),
        startTime: form.startTime,
        endTime: form.endTime,
      });
      setPreviewResults(results);
      setIsPreviewed(true);
      return results;
    } catch {
      setPreviewError("조회 중 오류가 발생했습니다.");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [form, buildRepeatDays, isFormValid]);

  /** 폼 변경으로 인한 조회. 예약 가능한 날짜를 전부 선택 상태로 리셋한다. */
  const doPreview = useCallback(async () => {
    const results = await requestPreview();
    if (!results) return;
    setIncludeDates(new Set(results.filter(r => r.available).map(r => r.date)));
  }, [requestPreview]);

  /**
   * 충돌 반려 후 재조회.
   * 새로 예약 가능해진 날짜는 자동 선택하되, 관리자가 직접 해제해 둔 날짜의
   * 상태는 보존한다. 그래서 전체 리셋이 아니라 이전 선택과 병합한다.
   */
  const refreshPreviewAfterReject = useCallback(async () => {
    if (autoPreviewTimer.current) clearTimeout(autoPreviewTimer.current);

    const prevResults = previewResults;
    const results = await requestPreview();
    if (!results) return;

    const previouslyAvailable = new Set(
      prevResults.filter(r => r.available).map(r => r.date),
    );

    setIncludeDates(prev => {
      const validDates = new Set(results.map(r => r.date));
      // 결과에서 사라진 날짜 제거
      const next = new Set([...prev].filter(date => validDates.has(date)));
      // 반려로 새로 가능해진 날짜만 추가
      results
        .filter(r => r.available && !previouslyAvailable.has(r.date))
        .forEach(r => next.add(r.date));
      return next;
    });
  }, [requestPreview, previewResults]);

  // 폼 변경 시 자동 미리보기 (디바운스)
  useEffect(() => {
    if (autoPreviewTimer.current) {
      clearTimeout(autoPreviewTimer.current);
    }
    if (isFormValid()) {
      autoPreviewTimer.current = setTimeout(() => {
        doPreview();
      }, 500);
    }
    return () => {
      if (autoPreviewTimer.current) clearTimeout(autoPreviewTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.plcCd,
    form.startDate,
    form.endDate,
    form.repeatType,
    form.repeatDays,
    form.monthlyDays,
    form.startTime,
    form.endTime,
  ]);

  const toggleInclude = (date: string) => {
    setIncludeDates(prev => {
      const next = new Set(prev);
      if (next.has(date)) {
        next.delete(date);
      } else {
        next.add(date);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    const allAvailableDates = previewResults
      .filter(r => r.available)
      .map(r => r.date);
    const isAllSelected =
      allAvailableDates.length > 0 &&
      allAvailableDates.every(d => includeDates.has(d));
    setIncludeDates(isAllSelected ? new Set() : new Set(allAvailableDates));
  };

  const handleSubmit = async () => {
    if (!form.purpose.trim()) {
      showToast("사용 목적을 입력해주세요.", "info");
      return;
    }

    const skipDates = previewResults
      .map(r => r.date)
      .filter(d => !includeDates.has(d));

    setIsLoading(true);
    try {
      await batchCreate({
        cpsCd: "PTK",
        bldCd: form.bldCd,
        plcCd: form.plcCd,
        startDate: dayjs(form.startDate).format("YYYYMMDD"),
        endDate: dayjs(form.endDate).format("YYYYMMDD"),
        repeatType: form.repeatType,
        repeatDays: buildRepeatDays(),
        startTime: form.startTime,
        endTime: form.endTime,
        groupName: `${form.purpose} (${dayjs(form.startDate).format("YYYY.MM.DD")}~${dayjs(form.endDate).format("YYYY.MM.DD")})`,
        useCnts: form.purpose,
        mbrId: tokenId,
        skipDates,
      });
      showToast("일괄 예약이 등록되었습니다.", "success");
      setForm(initialForm);
      resetPreview();
      setIncludeDates(new Set());
    } catch {
      showToast("일괄 예약 등록 중 오류가 발생했습니다.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const conflictCount = previewResults.reduce(
    (sum, result) => sum + result.conflicts.length,
    0,
  );

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 pb-8 pt-4">
      {/* 안내 문구 */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-small leading-relaxed text-gray-700">
        <p>일괄 예약은 ADMIN 사용자에게만 노출됩니다.</p>
        <p>
          특정 기간 예약을 한번에 동일한 내용으로 걸고 싶은 경우 사용하시면
          됩니다.
        </p>
        <p>
          만약 해당 기간 내 예약 내역이 있는 경우, 아래 리스트에서 확인 가능하며
          &lsquo;충돌 N건 관리&rsquo;에서 선택해 반려할 수 있습니다.
        </p>
      </div>

      {/* 건물 선택 */}
      <div className="flex flex-col gap-1.5">
        <label className="text-small font-medium text-primary">건물 선택</label>
        <div className="flex gap-3">
          {BUILDINGS.map(({ code, label }) => (
            <button
              key={code}
              type="button"
              className={`rounded-lg px-3 py-1.5 text-small font-medium transition-colors duration-150 ${
                form.bldCd === code
                  ? "bg-primary text-white shadow-sm"
                  : "bg-gray-light text-gray-dark hover:bg-gray-tinted"
              }`}
              onClick={() => handleChange("bldCd", code)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* 장소 선택 */}
      <div className="flex flex-col gap-1.5">
        <label className="text-small font-medium text-primary">장소 선택</label>
        <select
          className={inputCls}
          value={form.plcCd}
          onChange={e => handleChange("plcCd", e.target.value)}
        >
          <option value="">-- 장소를 선택하세요 --</option>
          {flatPlaces.map(place => (
            <option key={place.plcCd} value={place.plcCd}>
              {place.plcNm || place.plcCd}
            </option>
          ))}
        </select>
      </div>

      {/* 날짜 범위 */}
      <div className="flex flex-col gap-1.5">
        <label className="text-small font-medium text-primary">날짜 범위</label>
        <div className="flex items-center gap-3">
          <input
            type="date"
            className={`${inputCls} flex-1`}
            value={form.startDate}
            onChange={e => handleChange("startDate", e.target.value)}
          />
          <span className="text-small text-gray-dark">~</span>
          <input
            type="date"
            className={`${inputCls} flex-1`}
            value={form.endDate}
            min={form.startDate}
            onChange={e => handleChange("endDate", e.target.value)}
          />
        </div>
      </div>

      {/* 반복 유형 */}
      <div className="flex flex-col gap-1.5">
        <label className="text-small font-medium text-primary">반복 유형</label>
        <div className="flex gap-4">
          {(["daily", "weekly", "monthly"] as RepeatType[]).map(type => (
            <label
              key={type}
              className="flex cursor-pointer items-center gap-1.5 text-small"
            >
              <input
                type="radio"
                name="repeatType"
                value={type}
                checked={form.repeatType === type}
                onChange={() => handleChange("repeatType", type)}
                className="accent-primary"
              />
              {type === "daily" ? "매일" : type === "weekly" ? "매주" : "매월"}
            </label>
          ))}
        </div>
      </div>

      {/* 반복 요일 (weekly) */}
      {form.repeatType === "weekly" && (
        <div className="flex flex-col gap-1.5">
          <label className="text-small font-medium text-primary">
            반복 요일
          </label>
          <div className="flex flex-wrap gap-3">
            {Object.entries(DAY_OF_WEEK_LABEL).map(([value, label]) => (
              <label
                key={value}
                className="flex cursor-pointer items-center gap-1 text-small"
              >
                <input
                  type="checkbox"
                  value={value}
                  checked={form.repeatDays.includes(value)}
                  onChange={() => toggleRepeatDay(value)}
                  className="accent-primary"
                />
                {label}
              </label>
            ))}
          </div>
        </div>
      )}

      {/* 반복 일 (monthly) */}
      {form.repeatType === "monthly" && (
        <div className="flex flex-col gap-1.5">
          <label className="text-small font-medium text-primary">
            반복 날짜 (쉼표로 구분)
          </label>
          <input
            type="text"
            className={inputCls}
            placeholder="예: 1,15"
            value={form.monthlyDays}
            onChange={e => handleChange("monthlyDays", e.target.value)}
          />
        </div>
      )}

      {/* 시간 선택 */}
      <div className="flex flex-col gap-1.5">
        <label className="text-small font-medium text-primary">시간 선택</label>
        <div className="flex items-center gap-3">
          <select
            className={`${inputCls} flex-1`}
            value={form.startTime}
            onChange={e => {
              const newStart = e.target.value;
              const nextHourIdx = TIME_OPTIONS.indexOf(newStart) + 1;
              const autoEnd =
                TIME_OPTIONS[nextHourIdx] ??
                TIME_OPTIONS[TIME_OPTIONS.length - 1];
              setForm(prev => ({
                ...prev,
                startTime: newStart,
                endTime: prev.endTime <= newStart ? autoEnd : prev.endTime,
              }));
              resetPreview();
            }}
          >
            {TIME_OPTIONS.map(t => (
              <option key={t} value={t}>
                {formatTime(t)}
              </option>
            ))}
          </select>
          <span className="text-small text-gray-dark">~</span>
          <select
            className={`${inputCls} flex-1`}
            value={form.endTime}
            onChange={e => handleChange("endTime", e.target.value)}
          >
            {TIME_OPTIONS.filter(t => t > form.startTime).map(t => (
              <option key={t} value={t}>
                {formatTime(t)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* 사용 목적 */}
      <div className="flex flex-col gap-1.5">
        <label className="text-small font-medium text-primary">사용 목적</label>
        <input
          type="text"
          className={inputCls}
          placeholder="예: 여성예배 스텝 모임"
          value={form.purpose}
          onChange={e => handleChange("purpose", e.target.value)}
        />
      </div>

      {/* 오류 메시지 */}
      {previewError && (
        <p className="text-small text-red-500">{previewError}</p>
      )}

      {/* 가능한 날짜 보기 버튼 */}
      <div>
        <Button
          variant="outlined"
          onClick={doPreview}
          disabled={isLoading || !isFormValid()}
          className="text-small"
        >
          {isLoading && !isPreviewed ? "조회 중..." : "가능한 날짜 보기"}
        </Button>
      </div>

      {/* 미리보기 결과 */}
      {isPreviewed && previewResults.length > 0 && (
        <BatchPreviewTable
          previewResults={previewResults}
          includeDates={includeDates}
          conflictCount={conflictCount}
          isLoading={isLoading}
          onToggleInclude={toggleInclude}
          onToggleSelectAll={toggleSelectAll}
          onOpenConflictModal={() => setIsConflictModalOpen(true)}
          onSubmit={handleSubmit}
        />
      )}

      {isPreviewed && previewResults.length === 0 && (
        <p className="py-4 text-center text-small text-gray-500">
          해당 조건에 맞는 날짜가 없습니다.
        </p>
      )}

      {isConflictModalOpen && (
        <ConflictManageModal
          previewResults={previewResults}
          onClose={() => setIsConflictModalOpen(false)}
          onRejected={refreshPreviewAfterReject}
        />
      )}
    </div>
  );
};

export default BatchReservationTab;
